'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FlowNodeInspector from './FlowNodeInspector';

type NodeType = 'activity' | 'kpi' | 'bvo' | 'outcome';
type NodeHealth = 'good' | 'warn' | 'bad' | 'neutral';

interface FlowNode {
  id: string;
  label: string;
  sublabel?: string;
  type: NodeType;
  health: NodeHealth;
  x: number;
  y: number;
  width: number;
  height: number;
  value?: string;
}

interface FlowEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
  throughput?: string;
}

const nodes: FlowNode[] = [
  // Row 1 — Activities
  { id: 'n-po', label: 'Purchase Order', sublabel: 'Procurement', type: 'activity', health: 'good', x: 60, y: 60, width: 140, height: 56 },
  { id: 'n-grn', label: 'Goods Receipt', sublabel: 'Warehouse', type: 'activity', health: 'warn', x: 260, y: 60, width: 140, height: 56 },
  { id: 'n-inv', label: 'Supplier Invoice', sublabel: 'AP', type: 'activity', health: 'good', x: 460, y: 60, width: 140, height: 56 },
  { id: 'n-pay', label: 'Payment Run', sublabel: 'Treasury', type: 'activity', health: 'bad', x: 660, y: 60, width: 140, height: 56 },
  { id: 'n-rec', label: 'Cash Reconciliation', sublabel: 'Finance', type: 'activity', health: 'warn', x: 860, y: 60, width: 150, height: 56 },

  // Row 2 — KPIs
  { id: 'n-kpi-lead', label: 'PO Lead Time', sublabel: 'Avg 6.4 days', type: 'kpi', health: 'warn', x: 60, y: 190, width: 140, height: 52, value: '6.4d' },
  { id: 'n-kpi-match', label: 'Invoice Match Rate', sublabel: '3-way match', type: 'kpi', health: 'good', x: 320, y: 190, width: 150, height: 52, value: '88%' },
  { id: 'n-kpi-dpo', label: 'DPO (Days)', sublabel: 'Days Payable Outstanding', type: 'kpi', health: 'bad', x: 580, y: 190, width: 150, height: 52, value: '38d' },
  { id: 'n-kpi-disc', label: 'Discount Capture', sublabel: 'Early payment', type: 'kpi', health: 'warn', x: 840, y: 190, width: 150, height: 52, value: '61%' },

  // Row 3 — BVOs
  { id: 'n-bvo-sup', label: 'Supplier Invoice BVO', sublabel: 'Health: 78', type: 'bvo', health: 'warn', x: 160, y: 320, width: 170, height: 56 },
  { id: 'n-bvo-pay', label: 'Payment Operations BVO', sublabel: 'Health: 54', type: 'bvo', health: 'bad', x: 500, y: 320, width: 180, height: 56 },
  { id: 'n-bvo-wc', label: 'Working Capital BVO', sublabel: 'Health: 56', type: 'bvo', health: 'bad', x: 800, y: 320, width: 180, height: 56 },

  // Row 4 — Outcomes
  { id: 'n-out-cash', label: 'Cash Flow Optimization', sublabel: 'Business Outcome', type: 'outcome', health: 'warn', x: 200, y: 450, width: 200, height: 56 },
  { id: 'n-out-wc', label: 'Working Capital Improvement', sublabel: 'Business Outcome', type: 'outcome', health: 'bad', x: 620, y: 450, width: 220, height: 56 },
];

const edges: FlowEdge[] = [
  { id: 'e-po-grn', from: 'n-po', to: 'n-grn', throughput: '1,205/mo' },
  { id: 'e-grn-inv', from: 'n-grn', to: 'n-inv', throughput: '1,198/mo' },
  { id: 'e-inv-pay', from: 'n-inv', to: 'n-pay', throughput: '1,186/mo' },
  { id: 'e-pay-rec', from: 'n-pay', to: 'n-rec', throughput: '1,140/mo' },
  { id: 'e-po-kpi-lead', from: 'n-po', to: 'n-kpi-lead' },
  { id: 'e-grn-kpi-match', from: 'n-grn', to: 'n-kpi-match' },
  { id: 'e-inv-kpi-match', from: 'n-inv', to: 'n-kpi-match' },
  { id: 'e-pay-kpi-dpo', from: 'n-pay', to: 'n-kpi-dpo' },
  { id: 'e-rec-kpi-disc', from: 'n-rec', to: 'n-kpi-disc' },
  { id: 'e-kpi-lead-bvo', from: 'n-kpi-lead', to: 'n-bvo-sup' },
  { id: 'e-kpi-match-bvo', from: 'n-kpi-match', to: 'n-bvo-sup' },
  { id: 'e-kpi-dpo-bvo', from: 'n-kpi-dpo', to: 'n-bvo-pay' },
  { id: 'e-kpi-disc-bvo', from: 'n-kpi-disc', to: 'n-bvo-wc' },
  { id: 'e-bvo-sup-out', from: 'n-bvo-sup', to: 'n-out-cash' },
  { id: 'e-bvo-pay-out', from: 'n-bvo-pay', to: 'n-out-cash' },
  { id: 'e-bvo-pay-wc', from: 'n-bvo-pay', to: 'n-out-wc' },
  { id: 'e-bvo-wc-out', from: 'n-bvo-wc', to: 'n-out-wc' },
];

const nodeStyleMap: Record<NodeType, { bg: string; border: string; headerBg: string; textColor: string; typeLabel: string; typeColor: string }> = {
  activity: { bg: '#0f2040', border: '#1d4ed860', headerBg: '#1e3a5f', textColor: '#93c5fd', typeLabel: 'Activity', typeColor: '#3b82f6' },
  kpi: { bg: '#051a26', border: '#06b6d450', headerBg: '#0a2535', textColor: '#67e8f9', typeLabel: 'KPI', typeColor: '#06b6d4' },
  bvo: { bg: '#1a0f30', border: '#8b5cf650', headerBg: '#2d1f4e', textColor: '#c4b5fd', typeLabel: 'BVO', typeColor: '#8b5cf6' },
  outcome: { bg: '#0a2018', border: '#10b98150', headerBg: '#1a3a2a', textColor: '#6ee7b7', typeLabel: 'Outcome', typeColor: '#10b981' },
};

const healthDot: Record<NodeHealth, string> = {
  good: 'var(--positive)',
  warn: 'var(--warning)',
  bad: 'var(--negative)',
  neutral: 'var(--muted-foreground)',
};

function getNodeCenter(node: FlowNode) {
  return { cx: node.x + node.width / 2, cy: node.y + node.height / 2 };
}

const flowLibrary = [
  { id: 'lib-o2c', name: 'Order-to-Cash', bvos: 4, status: 'active' },
  { id: 'lib-p2p', name: 'Procure-to-Pay', bvos: 3, status: 'active' },
  { id: 'lib-r2r', name: 'Record-to-Report', bvos: 2, status: 'draft' },
  { id: 'lib-h2r', name: 'Hire-to-Retire', bvos: 2, status: 'draft' },
];

export default function FlowStudio() {
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [activeFlow, setActiveFlow] = useState('lib-p2p');
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(true);

  return (
    <div className="flex gap-4 h-[calc(100vh-160px)]">
      {/* Left Sidebar — Flow Library */}
      <div className="w-56 shrink-0 bg-card rounded-xl card-glow flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold text-foreground">Flow Library</p>
          <p className="text-2xs text-muted-foreground mt-0.5">Select a value chain to visualize</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {flowLibrary.map((flow) => (
            <button
              key={flow.id}
              onClick={() => setActiveFlow(flow.id)}
              className={`w-full text-left px-3 py-3 rounded-lg mb-1 transition-all duration-150 ${
                activeFlow === flow.id ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted'
              }`}
            >
              <p className={`text-xs font-medium ${activeFlow === flow.id ? 'text-primary' : 'text-foreground'}`}>{flow.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xs text-muted-foreground">{flow.bvos} BVOs</span>
                <span className={`text-2xs px-1.5 py-0.5 rounded ${flow.status === 'active' ? 'bg-positive/15 text-positive' : 'bg-muted text-muted-foreground'}`}>
                  {flow.status}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs font-semibold text-foreground mb-2">Node Types</p>
          {Object.entries(nodeStyleMap).map(([type, style]) => (
            <div key={`legend-type-${type}`} className="flex items-center gap-2 mb-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ background: style.bg, border: `1px solid ${style.border}` }} />
              <span className="text-2xs text-muted-foreground capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 min-w-0 bg-card rounded-xl card-glow flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setZoom((z) => Math.min(150, z + 10))}
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-surface-3 transition-colors"
                title="Zoom in"
              >
                <Icon name="MagnifyingGlassPlusIcon" size={14} />
              </button>
              <span className="text-xs text-muted-foreground w-10 text-center font-tabular">{zoom}%</span>
              <button
                onClick={() => setZoom((z) => Math.max(50, z - 10))}
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-surface-3 transition-colors"
                title="Zoom out"
              >
                <Icon name="MagnifyingGlassMinusIcon" size={14} />
              </button>
            </div>
            <button
              onClick={() => setZoom(100)}
              className="text-xs text-muted-foreground hover:text-foreground bg-muted px-2 py-1.5 rounded-lg transition-colors"
            >
              Reset
            </button>
            <button
              onClick={() => setShowGrid((g) => !g)}
              className={`text-xs px-2 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${showGrid ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name="Squares2X2Icon" size={12} />
              Grid
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-foreground">Procure-to-Pay</span>
            <span className="text-2xs bg-positive/15 text-positive px-2 py-0.5 rounded-full">Active</span>
          </div>

          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted px-3 py-1.5 rounded-lg border border-border transition-colors">
              <Icon name="ArrowDownTrayIcon" size={12} />
              Export
            </button>
            <button className="flex items-center gap-1.5 text-xs text-primary bg-primary/10 border border-primary/30 px-3 py-1.5 rounded-lg hover:bg-primary/20 transition-colors">
              <Icon name="PlusIcon" size={12} />
              Add Node
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto relative">
          <div
            className={showGrid ? 'canvas-bg' : 'bg-surface-1'}
            style={{ minWidth: '1100px', minHeight: '580px', position: 'relative' }}
          >
            <svg
              width="1100"
              height="580"
              viewBox="0 0 1100 580"
              style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', display: 'block' }}
            >
              <defs>
                <marker id="arrow-studio" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--muted-foreground)" opacity="0.5" />
                </marker>
                <marker id="arrow-studio-active" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--primary)" />
                </marker>
              </defs>

              {/* Edges */}
              {edges.map((edge) => {
                const fromNode = nodes.find((n) => n.id === edge.from);
                const toNode = nodes.find((n) => n.id === edge.to);
                if (!fromNode || !toNode) return null;
                const from = getNodeCenter(fromNode);
                const to = getNodeCenter(toNode);
                const isActive = selectedNode?.id === edge.from || selectedNode?.id === edge.to;
                const midX = (from.cx + to.cx) / 2;
                const midY = (from.cy + to.cy) / 2;
                return (
                  <g key={edge.id}>
                    <line
                      x1={from.cx}
                      y1={from.cy + fromNode.height / 2}
                      x2={to.cx}
                      y2={to.cy - toNode.height / 2}
                      stroke={isActive ? 'var(--primary)' : 'var(--border)'}
                      strokeWidth={isActive ? 1.5 : 1}
                      strokeDasharray={isActive ? 'none' : '5 3'}
                      markerEnd={isActive ? 'url(#arrow-studio-active)' : 'url(#arrow-studio)'}
                      opacity={isActive ? 1 : 0.6}
                    />
                    {edge.throughput && isActive && (
                      <text x={midX} y={midY - 6} fill="var(--primary)" fontSize="9" textAnchor="middle" fontFamily="var(--font-sans)">
                        {edge.throughput}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Nodes */}
              {nodes.map((node) => {
                const style = nodeStyleMap[node.type];
                const isSelected = selectedNode?.id === node.id;
                const isHovered = hoveredNode === node.id;
                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, ${node.y})`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedNode(isSelected ? null : node)}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                  >
                    {/* Shadow */}
                    {(isSelected || isHovered) && (
                      <rect
                        x="-2" y="-2"
                        width={node.width + 4}
                        height={node.height + 4}
                        rx="9"
                        fill="none"
                        stroke={isSelected ? style.typeColor : 'var(--border)'}
                        strokeWidth={isSelected ? 2 : 1}
                        opacity="0.8"
                      />
                    )}
                    {/* Card */}
                    <rect
                      x="0" y="0"
                      width={node.width}
                      height={node.height}
                      rx="8"
                      fill={style.bg}
                      stroke={isSelected ? style.typeColor : style.border}
                      strokeWidth={isSelected ? 1.5 : 1}
                    />
                    {/* Header strip */}
                    <rect
                      x="0" y="0"
                      width={node.width}
                      height="18"
                      rx="8"
                      fill={style.headerBg}
                    />
                    <rect x="0" y="10" width={node.width} height="8" fill={style.headerBg} />

                    {/* Type label */}
                    <text x="8" y="13" fill={style.typeColor} fontSize="8" fontFamily="var(--font-sans)" fontWeight="600" opacity="0.9">
                      {style.typeLabel}
                    </text>

                    {/* Health dot */}
                    <circle cx={node.width - 10} cy="9" r="4" fill={healthDot[node.health]} />

                    {/* Label */}
                    <text x="8" y="33" fill={style.textColor} fontSize="10.5" fontFamily="var(--font-sans)" fontWeight="600">
                      {node.label}
                    </text>

                    {/* Sublabel */}
                    {node.sublabel && (
                      <text x="8" y="47" fill="var(--muted-foreground)" fontSize="9" fontFamily="var(--font-sans)">
                        {node.sublabel}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Canvas footer */}
        <div className="px-4 py-2 border-t border-border flex items-center gap-4">
          <div className="flex items-center gap-3 text-2xs text-muted-foreground">
            <span>{nodes.length} nodes</span>
            <span>·</span>
            <span>{edges.length} connections</span>
            <span>·</span>
            <span>Last updated 4 min ago</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {[
              { color: 'var(--positive)', label: 'Healthy' },
              { color: 'var(--warning)', label: 'At Risk' },
              { color: 'var(--negative)', label: 'Critical' },
            ].map((h) => (
              <div key={`canvas-legend-${h.label}`} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ background: h.color }} />
                <span className="text-2xs text-muted-foreground">{h.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Inspector Panel */}
      {selectedNode && (
        <FlowNodeInspector node={selectedNode} onClose={() => setSelectedNode(null)} />
      )}
    </div>
  );
}